/**
 * The UI contract (plan §2.8): the station, title, death and ownership screens (M2-18) render `UiState` and call
 * `UiActions`; the career runtime (M2-22) owns the actions and pushes snapshots. Types only: no React or zustand
 * import (`UiStoreApi` is structurally zustand's vanilla `StoreApi<UiStore>`).
 *
 * Every action returns a `CmdOutcome` and never throws for a refusal. Session and save actions touch IndexedDB,
 * so they return a Promise (a documented M2-01 deviation from the plan's synchronous list).
 *
 * FROZEN after wave 1 (plan §1.2): later tasks may add optional members only.
 */
import type { GoodId } from '../career/parts'
import type { CommandError } from '../career/result'
import type { Career } from '../career/types'
import type { CraftCommand } from '../craft/hook'
import type { ModuleId, ModuleKind, SlotId, StationId, WareId } from '../data/m2Schemas'
import type { Quote } from '../economy/quote'
import type { MarketState } from '../space/contracts'
import type { MarketQuote } from '../strategic-link/market'

export type UiScreen = 'none' | 'title' | 'station' | 'death' | 'ownership'

export type StationTab = 'market' | 'outfitting' | 'repair' | 'lease' | 'contracts' | 'saves'
/** Tab order (Q/E and LB/RB step through it; `lease` shows only at a station with the lease service). */
export const STATION_TABS: readonly StationTab[] = ['market', 'outfitting', 'repair', 'lease', 'contracts', 'saves']

export type ManualSlotId = 'slot1' | 'slot2' | 'slot3'
export type AutoSlotId = 'auto1' | 'auto2' | 'auto3'
export type SaveSlotId = ManualSlotId | AutoSlotId
export const MANUAL_SLOTS: readonly ManualSlotId[] = ['slot1', 'slot2', 'slot3']
/** Rolling order: a new autosave goes to `auto1`, the old `auto1` moves to `auto2`, and so on. */
export const AUTO_SLOTS: readonly AutoSlotId[] = ['auto1', 'auto2', 'auto3']
export const SAVE_SLOTS: readonly SaveSlotId[] = [...MANUAL_SLOTS, ...AUTO_SLOTS]

/** One save slot as the Load and Saves screens list it. */
export interface SaveSlotInfo {
  slot: SaveSlotId
  /** `corrupt`: fails validation or migration (never overwritten); `future`: saved by a newer build. */
  status: 'empty' | 'ok' | 'corrupt' | 'future'
  /** ISO time of the save. */
  savedAt?: string
  gameTime?: number
  wallet?: number
  principal?: number
  station?: StationId
  /** Why a slot is corrupt or future (player-facing). */
  error?: string
}

export type StorageKind = 'indexeddb' | 'localstorage' | 'none'

/** A toast or banner line (comms subtitles carry their speaker). */
export interface UiNotice {
  id: number
  kind: 'info' | 'success' | 'warning' | 'error'
  text: string
  /** "Tethys Crew Finance", a station controller, ... */
  speaker?: string
  /** Game time it was raised. */
  at: number
}

/** The death screen ("Rebuilt at <station>: rebuild fee 17,400 added to your lease; your cargo is in your wreck"). */
export interface DeathReport {
  station: StationId
  /** Rebuild fee charged (after any insurance discount). */
  fee: number
  /** Repo fee charged (0 unless a repo crew was active). */
  repoFee: number
  /** True before ownership (the fee went onto the principal), false when it came from the wallet. */
  addedToPrincipal: boolean
  /** New insurer debt from a wallet shortfall (post-ownership). */
  debtIncurred: number
  /** Base-price estimate of the cargo left in the death wreck. */
  cargoValue: number
  /**
   * M6 (D-459 bug 5): the player died flying a fleet ship, not the lease ship. `lost`: it was uninsured and is
   * gone for good (the pilot is back in the lease ship, no rebuild fee); else it was rebuilt at the fee (halved when
   * insured). Absent: the lease ship died (the M2 wording).
   */
  fleetShip?: { lost: boolean; insured: boolean }
}

/** A confirmation the runtime asks for (e.g. New career when saves exist); answered with `answerConfirm`. */
export interface ConfirmRequest {
  id: string
  title: string
  message: string
  confirmLabel: string
  cancelLabel: string
}

export interface UiState {
  screen: UiScreen
  tab: StationTab
  /** Docked station, or null in space. */
  stationId: StationId | null
  /** Snapshot of the Career (null before one is created or loaded). */
  career: Career | null
  market: MarketState | null
  /** Game time of the snapshot (the runtime clock, which runs ahead of `career.gameTime` in space). */
  gameTime: number
  saves: SaveSlotInfo[]
  /** Where saves go; `localstorage` shows the banner "Progress may not persist in this browser mode." */
  storage: StorageKind
  /** Deferred station and prop models have loaded (Launch is enabled). */
  assetsReady: boolean
  /** 0..1 progress of the deferred models ("Preparing launch… 64%"). */
  assetsProgress: number
  /** A save is loading (the load hold): the screens ignore input and Launch waits until the career has docked. */
  loading: boolean
  notices: UiNotice[]
  /** The last refused command (shown inline until the next action). */
  lastError: CommandError | null
  deathReport: DeathReport | null
  confirm: ConfirmRequest | null
  /** M10 P5a8a: the moored tender run (set only with `moor.json tender.enabled` on; absent today). */
  tender?: TenderView
}

/** M10 P5a8a: the tender strip on the market panel ("Tender: n t queued", the run's bar). */
export interface TenderView {
  /** Tonnes queued for the next run. */
  queuedT: number
  /** Lines queued. */
  lines: number
  /** A run is out. */
  running: boolean
  /** The run's progress, 0..1. */
  progress: number
  /** Seconds left on the run. */
  remainingS: number
}

export type CmdOutcome = { ok: true } | { ok: false; error: CommandError }

/** An exported save: the file name (`umbra-reach-<slot>-<date>.json`) and its pretty JSON. */
export interface SaveExport {
  fileName: string
  text: string
}

export interface UiActions {
  // Session and saves (async: storage).
  newCareer(): Promise<CmdOutcome>
  /** Loads the newest valid save (also "load the newest valid autosave instead" on the Load screen). */
  continueCareer(): Promise<CmdOutcome>
  loadSlot(slot: SaveSlotId): Promise<CmdOutcome>
  /** Validates, migrates and stores `text` in `slot` (default: the first empty manual slot, else a confirm). */
  importSave(text: string, slot?: ManualSlotId): Promise<CmdOutcome>
  exportSave(slot: SaveSlotId): Promise<SaveExport | null>
  saveTo(slot: ManualSlotId): Promise<CmdOutcome>
  answerConfirm(id: string, accept: boolean): Promise<CmdOutcome>
  // Station screens (sync).
  setTab(tab: StationTab): CmdOutcome
  sellWare(ware: WareId, tonnes: number): CmdOutcome
  buyWare(ware: WareId, tonnes: number): CmdOutcome
  /** Sells the hold component with `CargoComponent.id` `id`. */
  sellComponent(id: string): CmdOutcome
  sellAllCargo(): CmdOutcome
  /** Additive (M10 P5a8a): sends the queued moored trades as one tender run; refused unless moored with the key on. */
  sendTender?(): CmdOutcome
  /**
   * Additive (economy plan SY-2c): buys `qty` units of a good at the docked station through the strategic market
   * (validated now, executed by the worker; a later refusal shows as `lastError`). Goods trade only while the
   * strategic economy runs. With `career.json goods.tradeEnabled` off it refuses every good but the relay parts.
   */
  buyGood?(good: GoodId, qty: number): CmdOutcome
  /** Additive (SY-2c): sells `qty` units of a good at the docked station; refuses everything while the flag is off. */
  sellGood?(good: GoodId, qty: number): CmdOutcome
  /**
   * Additive (SY-2c): the strategic quote for `qty` units of a good at the docked station (unit, total, fills before
   * and after), or null when the station doesn't trade it on `side`, no strategic market runs, or the trade isn't
   * allowed (the goods flag).
   */
  quoteGood?(good: GoodId, qty: number, side: 'buy' | 'sell'): MarketQuote | null
  /**
   * Additive (core-loop plan CL-3): the same strategic quote at another station `stationId` of the current system
   * (no docking needed; a sync read of the client's live rows, no worker command), or null as `quoteGood`. The
   * Market's buy rows read it for their best sale in this system (live prices: the current system is covered, D-503).
   */
  quoteGoodAt?(stationId: string, good: GoodId, qty: number, side: 'buy' | 'sell'): MarketQuote | null
  /**
   * Additive (CL-3): `quote()` for `qty` t of ore `ware` at the docked station (what the Market's ore rows show:
   * strategic prices while they run, else M2's formula), or null when undocked or it isn't traded on `side`. A sync
   * read for the ore rows' after-trade line; no command.
   */
  quoteWare?(ware: WareId, qty: number, side: 'buy' | 'sell'): Quote | null
  /**
   * Additive (economy plan SY-3): breaks the hold component with `CargoComponent.id` `id` down into goods in the hold
   * at the docked station (`salvageYields × condition`; nothing goes to the station). Refused while
   * `career.json goods.tradeEnabled` is off.
   */
  breakDown?(id: string): CmdOutcome
  /**
   * Additive (economy plan SY-5b): reconditions the Serviceable hold component with `CargoComponent.id` `id` to
   * Intact at the docked outfitting station, paid only in goods from the hold (`salvage.json recondition`, no
   * credits). Refused while `career.json goods.tradeEnabled` is off.
   */
  recondition?(id: string): CmdOutcome
  /**
   * Additive (economy plan SY-7): delivers the generic data core with `CargoComponent.id` `id` from the hold to the
   * docked station for standing with its faction, no credits (`salvage.json coreDelivery`; that faction's market
   * stations only). The core's log stays in the ledger. Story cores are refused. Not gated by the goods flag.
   */
  deliverCore?(id: string): CmdOutcome
  /**
   * Additive (economy plan SY-5c, over SY-5a): installs the Intact Mk-carrying hold component `id` as a salvaged
   * module into `ship.salvaged.locker` at the docked outfitting station (no fee); a weapon installs as `weaponKind`
   * (`pdc` or `laser`, default pdc). Not gated by the goods flag.
   */
  installComponent?(id: string, weaponKind?: ModuleKind): CmdOutcome
  /** Additive (SY-5c): fits the salvaged module `sourceId` into `slot`; the replaced module goes back to its locker. */
  fitSalvaged?(slot: SlotId, sourceId: string): CmdOutcome
  /** Additive (SY-5c): sells the unfitted salvaged module `sourceId` at component value × the best station multiplier. */
  sellSalvaged?(sourceId: string): CmdOutcome
  /**
   * Additive (M8 plan MC-0b): runs a craft command (berth, fabrication, study, Rush spares, data-core decode) through
   * the handler its feature registered on the craft hook (`craft/hook.ts`). Refused when no career runs or no handler
   * is registered for `cmd.kind`. `CraftCommand` grows by declaration merging (`CraftCommandMap`).
   */
  craft?(cmd: CraftCommand): CmdOutcome
  buyModule(id: ModuleId): CmdOutcome
  sellModule(id: ModuleId): CmdOutcome
  fitModule(slot: SlotId, id: ModuleId): CmdOutcome
  repair(points: number | 'all'): CmdOutcome
  rearm(count: number | 'all'): CmdOutcome
  payArrears(): CmdOutcome
  payExtra(amount: number): CmdOutcome
  setInsurance(on: boolean): CmdOutcome
  acceptContract(id: string): CmdOutcome
  abandonContract(id: string): CmdOutcome
  handInContract(id: string): CmdOutcome
  /** Leaves the station (the Launch button requests pointer lock synchronously before calling this). */
  launch(): CmdOutcome
  dismissNotice(id: number): CmdOutcome
  /** Closes the death or ownership screen (back to the station screen). */
  dismissScreen(): CmdOutcome
}

export type UiStore = UiState & { actions: UiActions }

/** Structurally zustand 5's vanilla `StoreApi<UiStore>` (M2-18 creates it with `createStore`). */
export interface UiStoreApi {
  getState(): UiStore
  getInitialState(): UiStore
  setState(partial: UiStore | Partial<UiStore> | ((state: UiStore) => UiStore | Partial<UiStore>), replace?: false): void
  subscribe(listener: (state: UiStore, prevState: UiStore) => void): () => void
}

